import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors,
  Trees,
  Leaf,
  Sprout,
  Droplets,
  TreeDeciduous,
  Layers,
  MoveDiagonal,
  Fence,
  BrickWall,
  Landmark,
  Footprints,
  Flame,
  Grip,
  Flower2,
  Snowflake,
  MoveHorizontal,
  PlusCircle,
  ArrowLeft,
  Phone
} from 'lucide-react';
import { Button } from './ui/button';
import { services, companyInfo, images } from '../data/mock';

const iconMap = {
  scissors: Scissors,
  trees: Trees,
  leaf: Leaf,
  sprout: Sprout,
  droplets: Droplets,
  'tree-deciduous': TreeDeciduous,
  layers: Layers,
  'move-diagonal': MoveDiagonal,
  fence: Fence,
  'brick-wall': BrickWall,
  landmark: Landmark,
  footprints: Footprints,
  flame: Flame,
  grip: Grip,
  'flower-2': Flower2,
  snowflake: Snowflake,
  'move-horizontal': MoveHorizontal,
  'plus-circle': PlusCircle
};

const ServicesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-bg-page">
      {/* Header */}
      <div className="bg-brand-dark py-6 px-4">
        <div className="container mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-brand-primary transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back</span>
          </button>
          <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-brand-primary font-semibold">
            <Phone size={18} />
            {companyInfo.phone}
          </a>
        </div>
      </div>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-brand-primary/10 border border-brand-primary/30 rounded-full px-4 py-2 mb-6">
            <span className="text-brand-dark text-sm font-semibold">
              What We Offer
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Our Services
          </h1>
          <p className="text-text-secondary text-lg leading-relaxed">
            From routine maintenance to complete outdoor transformations, we
            offer comprehensive services to meet all your landscaping needs.
          </p>
        </div>

        {/* All Services Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 mb-16">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || PlusCircle;
            return (
              <div
                key={service.id}
                className="group bg-bg-card rounded-2xl p-4 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-brand-primary/20"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:bg-brand-primary group-hover:scale-110 transition-all duration-300">
                  <IconComponent
                    className="text-brand-dark group-hover:text-white transition-colors"
                    size={20}
                  />
                </div>
                <h3 className="font-semibold text-text-primary group-hover:text-brand-dark transition-colors text-sm md:text-base">
                  {service.name}
                </h3>
              </div>
            );
          })}
        </div>

        {/* Featured Services Images */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="relative rounded-[32px] overflow-hidden h-[250px] md:h-[300px] group">
            <img
              src={images.hardscape}
              alt="Limestone Retaining Wall"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="text-white text-xl md:text-2xl font-bold mb-2">
                Retaining Walls & Grading
              </h3>
              <p className="text-white/80 text-sm md:text-base">
                Expert limestone retaining walls, grading solutions, and drainage systems.
              </p>
            </div>
          </div>
          <div className="relative rounded-[32px] overflow-hidden h-[250px] md:h-[300px] group">
            <img
              src={images.lawnMower}
              alt="Landscape Lighting"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="text-white text-xl md:text-2xl font-bold mb-2">
                Hardscapes & Outdoor Living
              </h3>
              <p className="text-white/80 text-sm md:text-base">
                Beautiful patios, walkways, and outdoor spaces with landscape lighting.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-brand-dark rounded-[32px] p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Contact us today for a free estimate on any of our services!
          </p>
          <a
            href={`tel:${companyInfo.phone}`}
            className="inline-flex items-center gap-3 bg-brand-primary text-brand-dark hover:bg-brand-primary/90 rounded-full px-8 py-4 text-lg font-bold transition-all duration-300 hover:-translate-y-1"
          >
            <Phone size={22} />
            {companyInfo.phone}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
